import React, { useEffect, useState } from "react";
import axios from "../axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import ArchiveModal from "../components/ArchiveModal";
import "../styles/admin.css";

dayjs.extend(utc);
dayjs.extend(timezone);

const toJST = (val) => (val ? dayjs.utc(val).tz("Asia/Tokyo") : null);
const fmtDate = (val) => toJST(val)?.format("MM/DD HH:mm") ?? "(不明)";

const toSightingObj = (r) => ({
  postId: r.post_id,
  route_full_name: r.sighting?.route?.full_name,
  route: r.sighting?.route?.short_name,
  start_full_name: r.sighting?.start?.full_name,
  start: r.sighting?.start?.short_name,
  time: toJST(r.sighting?.started_at)?.format("HH:mm") ?? "",
  comment: r.sighting?.comment ?? null,
  userId: r.sighting?.user?.id ?? null,
  user: r.sighting?.user?.name ?? "ゲスト",
  postedAt: toJST(r.sighting?.posted_at)?.format("MM/DD HH:mm") ?? null,
});

export default function AdminReportPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [modalTarget, setModalTarget] = useState(null);

  const toggleExpand = (postId) =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(postId) ? next.delete(postId) : next.add(postId);
      return next;
    });

  const fetchReports = () => {
    setLoading(true);
    axios.get("/reports").then((res) => setReports(res.data)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchReports(); }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`/admin/posts/${deleteTarget.post_id}`);
      setReports((prev) => prev.filter((r) => r.post_id !== deleteTarget.post_id));
      setDeleteTarget(null);
    } catch {
      alert("削除に失敗しました");
    } finally {
      setDeleting(false);
    }
  };

  const cutoff = dayjs().subtract(14, "day");
  const displayed = reports
    .filter((r) => r.last_reported && toJST(r.last_reported).isAfter(cutoff))
    .sort((a, b) => toJST(b.last_reported).valueOf() - toJST(a.last_reported).valueOf());

  return (
    <div className="adm-page">
      <AdminBreadcrumb items={[{ label: "通報一覧" }]} />

      <div className="adm-header">
        <div className="adm-header-left">
          <h2 className="adm-title">通報一覧</h2>
          <span className="adm-badge adm-badge--red">super_admin</span>
        </div>
      </div>

      {loading ? (
        <div className="adm-empty">読み込み中…</div>
      ) : (
        <div className="adm-card">
          <div className="adm-card__header">
            通報済み投稿
            <span className="adm-card__header-sub">
              {displayed.length} 件
              <span style={{ fontWeight: 400, marginLeft: "6px", color: "var(--adm-text-muted)" }}>
                （最終通報から14日以内 · 新着順）
              </span>
            </span>
          </div>

          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  {["投稿ID", "通報数", "最終通報", "通報者・理由", "操作"].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayed.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="adm-empty">14日以内の通報はありません</td>
                  </tr>
                ) : (
                  displayed.map((r) => (
                    <tr key={r.post_id}>
                      <td className="adm-table__id">{r.post_id}</td>
                      <td>
                        {r.count >= 5
                          ? <span className="adm-badge adm-badge--red">{r.count}</span>
                          : <span className="adm-table__bold">{r.count}</span>
                        }
                      </td>
                      <td className="adm-nowrap" style={{ color: "var(--adm-text-secondary)", fontWeight: 600 }}>
                        {fmtDate(r.last_reported)}
                      </td>
                      <td>
                        {r.reporters?.length > 0 ? (
                          <ReporterList
                            reporters={r.reporters}
                            expanded={expandedRows.has(r.post_id)}
                            onToggle={() => toggleExpand(r.post_id)}
                          />
                        ) : (
                          <span style={{ color: "var(--adm-text-muted)" }}>(なし)</span>
                        )}
                      </td>
                      <td>
                        <div className="adm-gap-sm">
                          <button
                            className="adm-btn adm-btn--default adm-btn--sm"
                            onClick={() => setModalTarget(r)}
                          >
                            詳細
                          </button>
                          <button
                            className="adm-btn adm-btn--danger adm-btn--sm"
                            onClick={() => setDeleteTarget(r)}
                          >
                            削除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 目撃詳細モーダル */}
      {modalTarget && (
        <ArchiveModal
          isOpen={true}
          onClose={() => setModalTarget(null)}
          depot={modalTarget.sighting?.bus?.depot?.short_name ?? ""}
          bus_number={modalTarget.sighting?.bus?.bus_number ?? ""}
          date={toJST(modalTarget.sighting?.started_at)?.format("YYYY-MM-DD") ?? ""}
          sightings={[toSightingObj(modalTarget)]}
        />
      )}

      {/* 削除確認モーダル */}
      {deleteTarget && (
        <div className="adm-overlay">
          <div className="adm-modal adm-modal--sm">
            <h3 className="adm-modal__title">削除の確認</h3>
            <p className="adm-modal__body">この投稿を削除しますか？この操作は取り消せません。</p>
            <p className="adm-modal__confirm-target">
              投稿ID: {deleteTarget.post_id}（{deleteTarget.sighting?.user?.name ?? "不明"}）
            </p>
            <div className="adm-modal__actions">
              <button className="adm-btn adm-btn--default" onClick={() => setDeleteTarget(null)}>
                キャンセル
              </button>
              <button className="adm-btn adm-btn--danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? "削除中…" : "削除する"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const PREVIEW_COUNT = 2;

function ReporterList({ reporters, expanded, onToggle }) {
  const visible = expanded ? reporters : reporters.slice(0, PREVIEW_COUNT);
  const rest = reporters.length - PREVIEW_COUNT;

  return (
    <ul style={{ margin: 0, padding: 0, fontSize: ".75rem", listStyle: "none" }}>
      {visible.map((rep, idx) => (
        <li key={idx} style={{ marginBottom: ".2rem", lineHeight: 1.4 }}>
          <span style={{ fontWeight: 600 }}>{rep.user_name}</span>
          {rep.reason && (
            <span style={{ color: "var(--adm-text-secondary)" }}>：{rep.reason}</span>
          )}
          <span style={{ color: "var(--adm-text-muted)", marginLeft: ".4rem" }}>
            {fmtDate(rep.created_at)}
          </span>
        </li>
      ))}
      {rest > 0 && (
        <li>
          <button
            onClick={onToggle}
            style={{
              background: "none", border: "none", padding: 0, cursor: "pointer",
              fontSize: ".72rem", color: "var(--adm-link-color, #4f7ef7)",
            }}
          >
            {expanded ? "▲ 折りたたむ" : `▼ 他${rest}件`}
          </button>
        </li>
      )}
    </ul>
  );
}
